import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  BookMarked,
  Cog,
  LayoutPanelTop, // Changed from SquareChartGantt which wasn't supported
  TextQuote,
  Search,
  ChevronRight,
  ChevronLeft
} from "lucide-react";
import TabBar from "./TabBar";
import SidePayWall from "./SidePayWall";
import Typography from "../Typography/Typography";

export default function Sidebar(props) {
  const { onUpgradeClick } = props;
  const [activeTab, setActiveTab] = useState(null);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(undefined);
  const [enableTransitions, setEnableTransitions] = useState(false);
  const [logoTransition, setLogoTransition] = useState({
    compact: isSidebarCollapsed, 
    opacity: 1
  });
  const initialRenderDone = useRef(false);
  const navigate = useNavigate();

  // Load sidebar state from localStorage on mount
  useEffect(() => {
    const savedState = localStorage.getItem("sidebarCollapsed");
    // Only set state if we have a saved preference
    if (savedState !== null) {
      const collapsed = savedState === "true";
      setIsSidebarCollapsed(collapsed);
      setLogoTransition({ compact: collapsed, opacity: 1 });
    } else {
      // If no saved state, use the prop as default
      setIsSidebarCollapsed(props.isSidebarCollapsed);
      setLogoTransition({ compact: props.isSidebarCollapsed, opacity: 1 });
    }

    // After initial render, mark as done
    setTimeout(() => {
      initialRenderDone.current = true;
    }, 100);
  }, [props.isSidebarCollapsed]);

  // Save sidebar state to localStorage whenever it changes
  useEffect(() => {
    if (isSidebarCollapsed !== undefined) {
      localStorage.setItem("sidebarCollapsed", isSidebarCollapsed);
      // Emit event to parent if needed
      if (props.onSidebarToggle) {
        props.onSidebarToggle(isSidebarCollapsed);
      }
    }
  }, [isSidebarCollapsed, props.onSidebarToggle]);

  useEffect(() => {
    const path = window.location.pathname.split("/").pop();
    const tabMap = {
      library: 0,
      research: 1,
      index: 2,
      roadmap: 3,
      settings: 4,
    };
    setActiveTab(tabMap[path] ?? null);
  }, []);

  const handleTabClick = (tabIndex, path) => {
    setActiveTab(tabIndex);
    navigate(`/client/${path}`);
  };

  const handleBorderClick = () => {
    // Enable transitions only when user manually toggles
    setEnableTransitions(true);
    
    // First fade out current logo
    setLogoTransition(prev => ({ ...prev, opacity: 0 }));
    
    // After logo starts fading, change the sidebar state
    setTimeout(() => {
      setIsSidebarCollapsed(!isSidebarCollapsed);
      
      // Change logo variant during sidebar animation
      setTimeout(() => {
        setLogoTransition(prev => ({ 
          compact: !isSidebarCollapsed, 
          opacity: 1
        }));
      }, 50);
      
      // Keep transitions enabled for a bit longer to ensure smooth completion
      setTimeout(() => {
        setEnableTransitions(false);
      }, 400); // Slightly longer than animation duration
    }, 50);
  };

  return (
    <>
      {/* Desktop Sidebar */}
      <div
        className={`hidden md:flex ${
          isSidebarCollapsed ? "w-20" : "w-60"
        } h-screen flex-col px-4 py-6 bg-gray-100 border-r-2 z-50 border-gray-150 justify-between relative ${
          enableTransitions ? "transition-all duration-300 ease-in-out" : ""
        }`}
      >
        <div className="flex flex-col gap-6">
          <div className="relative h-12">
            <img
              className={`absolute ${logoTransition.compact ? "w-12" : "md:w-32 w-12"} 
                transition-opacity duration-150 ease-in-out`}
              style={{ opacity: logoTransition.opacity }}
              src={
                logoTransition.compact
                  ? "/assets/images/logo/logo.svg"
                  : "/assets/images/logo/lockup.svg"
              }
              alt="Athena Logo"
            />
          </div>
          <div className="flex flex-col gap-1">
            <TabBar
              icon={<BookMarked size="20" />}
              text="Libreria"
              isActive={activeTab === 0}
              onClick={() => handleTabClick(0, "library")}
              haveText={!isSidebarCollapsed}
            />
            <TabBar
              icon={<Search size="20" />}
              text="Ricerca"
              isActive={activeTab === 1}
              onClick={() => handleTabClick(1, "research")}
              haveText={!isSidebarCollapsed}
            />
            <TabBar
              icon={<TextQuote size="20" />}
              text="Indice"
              isActive={activeTab === 2}
              onClick={() => handleTabClick(2, "index")}
              haveText={!isSidebarCollapsed}
            />
            <TabBar
              icon={<LayoutPanelTop size="20" />}
              text="Roadmap"
              isActive={activeTab === 3}
              onClick={() => handleTabClick(3, "roadmap")}
              haveText={!isSidebarCollapsed}
            />
          </div>
        </div>
        <div className="flex flex-col gap-6">
          <SidePayWall onClick={onUpgradeClick} />
          <TabBar
            icon={<Cog size="20" />}
            text="Impostazioni"
            isActive={activeTab === 4}
            onClick={() => handleTabClick(4, "settings")}
            haveText={!isSidebarCollapsed}
          />
        </div>
        <div
          className="absolute -right-8 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center cursor-pointer"
          onClick={handleBorderClick}
        >
          {isSidebarCollapsed ? 
            <ChevronRight size={20} className="text-gray-500" /> : 
            <ChevronLeft size={20} className="text-gray-500" />
          }
        </div>
      </div>

      {/* Mobile Bottom Navigation */}
      <div
        ref={props.ref}
        className="z-60 fixed bottom-0 w-full bg-gray-100 px-9 py-4 flex justify-around gap-9 md:hidden border-t-2 border-gray-150 pointer-events-auto"
      >
        <button
          onClick={() => handleTabClick(0, "library")}
          className={`${activeTab === 0 ? "text-black" : "text-gray-600"}`}
        >
          <div className="flex flex-col items-center gap-2">
            <BookMarked size="20" />
            <Typography
              weight="md"
              size="12"
              color={`${activeTab === 0 ? "black" : "gray-600"}`}
            >
              Libreria
            </Typography>
          </div>
        </button>
        <button
          onClick={() => handleTabClick(1, "research")}
          className={`${activeTab === 1 ? "text-black" : "text-gray-600"}`}
        >
          <div className="flex flex-col items-center gap-2">
            <Search size="20" />
            <Typography
              weight="md"
              size="12"
              color={`${activeTab === 1 ? "black" : "gray-600"}`}
            >
              Ricerca
            </Typography>
          </div>
        </button>
        <button
          onClick={() => handleTabClick(2, "index")}
          className={`${activeTab === 2 ? "text-black" : "text-gray-600"}`}
        >
          <div className="flex flex-col items-center gap-2">
            <TextQuote size="24" />
            <Typography
              weight="md"
              size="12"
              color={`${activeTab === 2 ? "black" : "gray-600"}`}
            >
              Indice
            </Typography>
          </div>
        </button>
        <button
          onClick={() => handleTabClick(3, "roadmap")}
          className={`${activeTab === 3 ? "text-black" : "text-gray-600"}`}
        >
          <div className="flex flex-col items-center gap-2">
            <LayoutPanelTop size="24" />
            <Typography
              weight="md"
              size="12"
              color={`${activeTab === 3 ? "black" : "gray-600"}`}
            >
              Roadmap
            </Typography>
          </div>
        </button>
      </div>
    </>
  );
}